import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { RandomAvatar } from 'react-random-avatar';
import CloseIcon from '@mui/icons-material/Close';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CommentIcon from '@mui/icons-material/Comment';
import FlagIcon from '@mui/icons-material/Flag';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NavigationIcon from '@mui/icons-material/Navigation';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDrag } from '@use-gesture/react';
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from 'motion/react';
import useEmblaCarousel from 'embla-carousel-react';
import EventImageGallery from '../components/EventImageGallery';
import EventImageSlideshow from '../components/EventImageSlideshow';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { getApiUrl } from '../apiUrl';
import UserIdentity from '../components/UserIdentity';

const EventSheet = ({ store, event, onClose, onUpdate, onProfileRefresh }) => {
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [slideshowPhotos, setSlideshowPhotos] = useState([]);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [slideshowDescription, setSlideshowDescription] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [eventMenuAnchor, setEventMenuAnchor] = useState(null);
  const [eventPosterHelpfulCount, setEventPosterHelpfulCount] = useState(0);
  const [updateMenuAnchors, setUpdateMenuAnchors] = useState({});
  const [flagAbuseDialog, setFlagAbuseDialog] = useState({ open: false, targetId: null, targetType: null });
  const [flagAbuseComment, setFlagAbuseComment] = useState('');
  const [ongoingMenuAnchor, setOngoingMenuAnchor] = useState(null);
  
  const sheetRef = useRef(null);
  const dragHandleRef = useRef(null);
  const y = useMotionValue(0);
  
  // Embla carousel for images
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true
  });

  if (!event || !store) return null;

  const API_URL = getApiUrl();

  // Helper to format helpfulCount (e.g., 4200 -> "4.2K", 150 -> "150", 0 -> "0")
  const formatHelpfulCount = (count) => {
    if (count === undefined || count === null) return "0";
    if (count === 0) return "0";
    if (count >= 1000) {
      const k = (count / 1000).toFixed(1);
      return k.endsWith('.0') ? `${k.slice(0, -2)}K` : `${k}K`;
    }
    return count.toString();
  };

  // Helper to format relative time (e.g., "19m ago", "14h ago", "7d ago")
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${diffYears}y ago`;
  };

  // Helper to convert R2.dev URLs or R2 paths to proxy URLs
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // If it's already a proxy URL, use as-is
    if (url.includes('/api/images/')) {
      return url;
    }
    
    // If it's an R2.dev URL, extract the key and convert to proxy
    if (url.includes('.r2.dev/')) {
      const keyMatch = url.match(/r2\.dev\/(.+)$/);
      if (keyMatch) {
        return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
      }
    }
    
    // If it's a Cloudflare UI path format (fire-sales-images/events/xxx.png)
    // or just the key (events/xxx.png), use it directly
    if (url.includes('events/')) {
      const keyMatch = url.match(/(?:fire-sales-images\/)?(events\/.+)$/);
      if (keyMatch) {
        return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
      }
    }
    
    // Otherwise return as-is (might be base64 or other format)
    return url;
  };

  // Get user avatar component
  const getUserAvatar = (user, size = 32) => {
    if (!user) {
      return (
        <Avatar sx={{ width: size, height: size }}>
          ?
        </Avatar>
      );
    }
    // Handle case where user might be just an ID string
    if (typeof user === 'string') {
      return (
        <Avatar sx={{ width: size, height: size }}>
          ?
        </Avatar>
      );
    }
    if (user.profilePhoto) {
      return <Avatar src={getImageUrl(user.profilePhoto)} sx={{ width: size, height: size }} />;
    }
    if (user.avatarSeed) {
      return (
        <Avatar sx={{ width: size, height: size }}>
          <RandomAvatar seed={user.avatarSeed} size={size / 8} />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ width: size, height: size }}>
        {user.username?.charAt(0).toUpperCase() || '?'}
      </Avatar>
    );
  };

  // Get current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/check-session`);
        if (response.data.isLoggedIn && response.data._id) {
          setCurrentUserId(response.data._id);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
    
    // Fetch event poster's helpfulCount - check if event.userId has helpfulCount populated
    if (event.userId) {
      // First check if helpfulCount is already populated in event.userId (from server populate)
      const helpfulCount = event.userId.helpfulCount;
      if (helpfulCount !== undefined && helpfulCount !== null) {
        setEventPosterHelpfulCount(helpfulCount);
      } else {
        // Otherwise fetch it
        const userId = event.userId._id || event.userId;
        const fetchPosterInfo = async () => {
          try {
            const response = await axios.get(`${API_URL}/api/user/${userId}`);
            if (response.data.user) {
              setEventPosterHelpfulCount(response.data.user.helpfulCount || 0);
            }
          } catch (error) {
            console.error('Error fetching poster info:', error);
          }
        };
        fetchPosterInfo();
      }
    }
  }, [event.userId, event.userId?.helpfulCount, API_URL]);

  // Countdown timer for expiration
  const applyOptimisticEvent = (updater) => {
    if (!onUpdate) return null;
    const base = JSON.parse(JSON.stringify(event));
    const next = updater(base);
    onUpdate(next);
    return next;
  };

  const maybeRefreshOwnProfile = (targetUserId) => {
    if (!onProfileRefresh || !currentUserId || !targetUserId) return;
    if (String(targetUserId) === String(currentUserId)) onProfileRefresh();
  };
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  useEffect(() => {
    if (!event.expiresAt) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const expires = new Date(event.expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeRemaining({ expired: true, text: 'Expired' });
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      let text = '';
      if (hours > 0) {
        text = `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        text = `${minutes}m ${seconds}s`;
      } else {
        text = `${seconds}s`;
      }
      
      setTimeRemaining({ expired: false, hours, minutes, seconds, text, diff });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [event.expiresAt]);

  // Spring animation for smooth movement
  const springY = useSpring(y, { stiffness: 300, damping: 30 });
  
  // Transform y position to opacity and translate
  const opacity = useTransform(y, [0, 200], [1, 0]);
  const translateY = useTransform(springY, [0, 500], [0, 500]);

  // Drag gesture handler
  const bind = useDrag(
    ({ movement: [, my], down, velocity: [, vy] }) => {
      if (down) {
        setIsDragging(true);
        y.set(Math.max(0, my));
      } else {
        setIsDragging(false);
        // If dragged down significantly or with velocity, close
        if (my > 150 || vy > 0.5) {
          y.set(500);
          setTimeout(() => onClose(), 200);
        } else {
          // Snap back
          y.set(0);
        }
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      rubberband: true
    }
  );

  // Combine all photos from event and updates
  const allPhotos = [
    ...(event.photos || []),
    ...(event.updates || []).flatMap(update => update.photos || [])
  ];

  const handleAddUpdate = async () => {
    if (!comment && photos.length === 0) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/updates`,
        {
          content: comment,
          photos
        }
      );

      if (response.status === 200) {
        setComment('');
        setPhotos([]);
        if (onUpdate) onUpdate(response.data.event || null);
      }
    } catch (error) {
      console.error('Error adding update:', error);
      alert('Error adding update: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOngoingVote = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/ongoing`,
        {}
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
      }
    } catch (error) {
      console.error('Error voting ongoing:', error);
      const message = error.response?.data?.error || error.message;
      if (message.includes('24 hours')) {
        alert('You can only vote once per 24 hours');
      } else {
        alert('Error: ' + message);
      }
    }
  };

  // Check if user can vote ongoing (hasn't voted in last 24 hours)
  const canVoteOngoing = useMemo(() => {
    if (!currentUserId || !event.ongoingVotes) return true;
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recentVote = event.ongoingVotes.find(vote => {
      return vote.userId?.toString() === currentUserId.toString() && 
             new Date(vote.votedAt) > oneDayAgo;
    });
    return !recentVote;
  }, [currentUserId, event.ongoingVotes]);

  const handleVote = async (updateIndex, isHelpful) => {
    const target = event.updates?.[updateIndex];
    if (isHelpful && target?.helpfulBy?.some((id) => String(id) === String(currentUserId))) return;

    applyOptimisticEvent((draft) => {
      const row = draft.updates?.[updateIndex];
      if (!row) return draft;
      if (isHelpful) {
        row.helpfulCount = (row.helpfulCount || 0) + 1;
        row.helpfulBy = [...(row.helpfulBy || []), currentUserId];
      } else {
        row.unhelpfulCount = (row.unhelpfulCount || 0) + 1;
        row.unhelpfulBy = [...(row.unhelpfulBy || []), currentUserId];
      }
      if (isHelpful && row.userId && String(typeof row.userId === 'object' ? row.userId._id : row.userId) === String(currentUserId)) {
        const u = typeof row.userId === 'object' ? row.userId : null;
        if (u) u.helpfulCount = (u.helpfulCount || 0) + 1;
      }
      return draft;
    });

    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/updates/${updateIndex}/vote`,
        { isHelpful }
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
        if (isHelpful) {
          const targetUserId = typeof target?.userId === 'object' ? target?.userId?._id : target?.userId;
          maybeRefreshOwnProfile(targetUserId);
        }
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Error voting: ' + (error.response?.data?.error || error.message));
      if (onUpdate) onUpdate(null);
    }
  };

  const handleHeartUpdate = async (updateIndex) => {
    const target = event.updates?.[updateIndex];
    if (target?.heartedBy?.some((h) => String(h.userId) === String(currentUserId))) return;

    applyOptimisticEvent((draft) => {
      const row = draft.updates?.[updateIndex];
      if (!row) return draft;
      row.heartedBy = [...(row.heartedBy || []), { userId: currentUserId, heartedAt: new Date().toISOString() }];
      const targetUserId = typeof row.userId === 'object' ? row.userId?._id : row.userId;
      if (targetUserId && String(targetUserId) === String(currentUserId) && typeof row.userId === 'object') {
        row.userId.lifetimeHearts = (row.userId.lifetimeHearts || 0) + 1;
        row.userId.helpfulCount = (row.userId.helpfulCount || 0) + 1;
      }
      return draft;
    });

    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/updates/${updateIndex}/heart`,
        {}
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
        const targetUserId = typeof target?.userId === 'object' ? target?.userId?._id : target?.userId;
        maybeRefreshOwnProfile(targetUserId);
      }
    } catch (error) {
      console.error('Error hearting update:', error);
      alert('Error hearting update: ' + (error.response?.data?.error || error.message));
      if (onUpdate) onUpdate(null);
    }
  };

  const handleHeartEvent = async () => {
    if (event.heartedBy?.some((h) => String(h.userId) === String(currentUserId))) return;

    applyOptimisticEvent((draft) => {
      draft.heartedBy = [...(draft.heartedBy || []), { userId: currentUserId, heartedAt: new Date().toISOString() }];
      const targetUserId = typeof draft.userId === 'object' ? draft.userId?._id : draft.userId;
      if (targetUserId && String(targetUserId) === String(currentUserId) && typeof draft.userId === 'object') {
        draft.userId.lifetimeHearts = (draft.userId.lifetimeHearts || 0) + 1;
        draft.userId.helpfulCount = (draft.userId.helpfulCount || 0) + 1;
      }
      return draft;
    });

    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/heart`,
        {}
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
        const targetUserId = typeof event.userId === 'object' ? event.userId?._id : event.userId;
        maybeRefreshOwnProfile(targetUserId);
      }
    } catch (error) {
      console.error('Error hearting event:', error);
      alert('Error hearting event: ' + (error.response?.data?.error || error.message));
      if (onUpdate) onUpdate(null);
    }
  };
  const handleUnhelpfulEvent = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/unhelpful`,
        {}
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
        setOngoingMenuAnchor(null);
      }
    } catch (error) {
      console.error('Error marking unhelpful:', error);
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };


  const handleFlagEventEnded = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/flag-ended`,
        {}
      );

      if (response.data.success) {
        setOngoingMenuAnchor(null);
        if (response.data.archived || response.data.ended) {
          if (onUpdate) onUpdate(response.data.event || null);
          onClose();
        } else if (onUpdate) {
          onUpdate(response.data.event || null);
        }
      }
    } catch (error) {
      console.error('Error flagging event as ended:', error);
    }
  };

  const handleFlagAbuse = async () => {
    try {
      const { targetId, targetType } = flagAbuseDialog;
      let endpoint;
      if (targetType === 'event') {
        endpoint = `/api/events/${targetId}/flag`;
      } else {
        endpoint = `/api/events/${event._id}/updates/${targetId}/flag`;
      }
      
      const response = await axios.post(
        `${API_URL}${endpoint}`,
        { comment: flagAbuseComment }
      );

      if (response.data.success) {
        setFlagAbuseDialog({ open: false, targetId: null, targetType: null });
        setFlagAbuseComment('');
        if (onUpdate) onUpdate(response.data.event || null);
        if (response.data.deleted) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error flagging abuse:', error);
    }
  };

  const handleUnhelpfulUpdate = async (updateIndex) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event._id}/updates/${updateIndex}/unhelpful`,
        {}
      );

      if (response.data.success && onUpdate) {
        onUpdate(response.data.event || null);
        setUpdateMenuAnchors({ ...updateMenuAnchors, [updateIndex]: null });
      }
    } catch (error) {
      console.error('Error marking update unhelpful:', error);
    }
  };

  const handleNavigate = () => {
    if (store.address?.fullAddress) {
      const address = encodeURIComponent(store.address.fullAddress);
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
    }
  };

  const uploadToR2 = async (file) => {
    try {
      const options = {
        maxSizeMB: 5,
        maxWidthOrHeight: 4000,
        useWebWorker: true
      };
      
      const compressedFile = await imageCompression(file, options);
      const formData = new FormData();
      formData.append('image', compressedFile, file.name);

      const response = await axios.post(
        `${API_URL}/api/upload-image`,
        formData
      );

      if (response.data.success) {
        return {
          url: response.data.photo.url,
          key: response.data.photo.key,
          uploadedAt: response.data.photo.uploadedAt
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading to R2:', error);
      throw error;
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    try {
      const uploadPromises = files.map(file => uploadToR2(file));
      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos([...photos, ...uploadedPhotos]);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setLoading(false);
      if (e.target) e.target.value = '';
    }
  };

  // Navigate to store address
  const canNavigate = store.address?.fullAddress;

  return (
    <>
    <motion.div
      ref={sheetRef}
        style={{
          position: 'fixed',
          bottom: 0,
          left: '1%',
          right: '1%',
          width: '98%',
          height: '92%',
          backgroundColor: 'white',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
          zIndex: 10003, // Above map markers (10000)
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        y: translateY,
        opacity: opacity
      }}
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
    >
        {/* Drag Handle Bar */}
        <Box
          ref={dragHandleRef}
          {...bind()}
          sx={{
            width: '100%',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            },
            position: 'relative',
            pt: 1
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: '#ccc',
              borderRadius: '2px'
            }}
          />
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            pb: 2
          }}
        >
          {/* Title and Header Row */}
          <Box sx={{ mb: 1.5, pt: 1 }}>
            {/* Combined Auto-expiration Countdown and Ongoing Vote */}
            {timeRemaining && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 1.5,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'text.primary' }}>
                    Auto-deletion:
                  </Typography>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={timeRemaining.text}
                      initial={{ scale: 1.2, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Chip
                        label={timeRemaining.expired ? 'Expired' : timeRemaining.text}
                        size="small"
                        color={timeRemaining.expired ? 'error' : (timeRemaining.hours !== undefined && timeRemaining.hours < 2) ? 'warning' : 'default'}
                        sx={{
                          fontWeight: (timeRemaining.hours !== undefined && timeRemaining.hours < 2) ? 600 : 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>
                    Ongoing?
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleOngoingVote}
                    disabled={!canVoteOngoing}
                    sx={{ 
                      color: canVoteOngoing ? '#4caf50' : '#9e9e9e',
                      padding: '4px 8px',
                      minWidth: '32px',
                      height: '24px',
                      fontSize: '0.875rem',
                      border: canVoteOngoing ? '1px solid #4caf50' : '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: canVoteOngoing ? 'transparent' : '#f5f5f5',
                      '&:hover': {
                        backgroundColor: canVoteOngoing ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                      },
                      '&:disabled': {
                        color: '#9e9e9e',
                        borderColor: '#e0e0e0'
                      }
                    }}
                  >
                    ✓
                  </IconButton>
                  {/* {!canVoteOngoing && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5 }}>
                      (24h)
                    </Typography>
                  )} */}
                  {/* Dropdown menu for unhelpful/flag/end (only for photo posts) */}
                  {event.photos && event.photos.length > 0 && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => setOngoingMenuAnchor(e.currentTarget)}
                        sx={{ padding: '4px', ml: 0.5 }}
                      >
                        <MoreVertIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                      <Menu
                        anchorEl={ongoingMenuAnchor}
                        open={Boolean(ongoingMenuAnchor)}
                        onClose={() => setOngoingMenuAnchor(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        sx={{ zIndex: 10006 }} // Above EventSheet
                        MenuListProps={{
                          sx: { zIndex: 10006 }
                        }}
                        PaperProps={{
                          sx: { zIndex: 10006 }
                        }}
                      >
                        <MenuItem onClick={handleFlagEventEnded}>
                          <FlagIcon sx={{ fontSize: '16px', mr: 1 }} />
                          Mark as Ended
                        </MenuItem>
                        <MenuItem onClick={() => {
                          setOngoingMenuAnchor(null);
                          setFlagAbuseDialog({ open: true, targetId: event._id, targetType: 'event' });
                        }}>
                          <FlagIcon sx={{ fontSize: '16px', mr: 1 }} />
                          Flag Abuse
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {/* Title at top */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {event.title}
            </Typography>
            
            {/* Compact header row: Avatar, Username, HelpfulCount, Photo Slideshow, Time, Heart */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'nowrap', width: '100%' }}>
              <UserIdentity
                user={typeof event.userId === 'object' ? event.userId : { username: event.username, _id: event.userId }}
                size="lg"
                variant="light"
              />
              
              {/* Single square photo slideshow element */}
              {event.photos && event.photos.length > 0 && (
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: 42,
                    height: 42,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                  onClick={() => {
                    // Open slideshow for original poster's photos
                    const originalPhotos = event.photos || [];
                    setSlideshowPhotos(originalPhotos);
                    setSlideshowIndex(0);
                    setShowSlideshow(true);
                    setSlideshowDescription(event.description);
                  }}
                >
                  <img
                    src={getImageUrl(event.photos[0].url)}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  {event.photos.length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        +{event.photos.length - 1}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Time stamp - positioned to the right of photo slideshow */}
              {event.createdAt && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.875rem', 
                    flexShrink: 0,
                    fontWeight: 500
                  }}
                >
                  {formatRelativeTime(event.createdAt)}
                </Typography>
              )}
              
              <Box sx={{ flex: 1, minWidth: 8 }} />
              
              {/* Heart button and count - bigger for original event */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                <IconButton
                  onClick={handleHeartEvent}
                  disabled={event.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString())}
                  sx={{ 
                    color: event.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString()) ? 'pink' : 'inherit',
                    padding: '0px',
                    '& .MuiSvgIcon-root': { fontSize: '24px' }
                  }}
                >
                  <FavoriteIcon />
                </IconButton>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1rem', minWidth: '20px', textAlign: 'center' }}>
                  {event.heartedBy?.length || 0}
                </Typography>
                {/* 3-dot menu for original post (only for photo posts) */}
                {event.photos && event.photos.length > 0 && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => setEventMenuAnchor(e.currentTarget)}
                      sx={{ padding: '4px', ml: 0 }}
                    >
                      <MoreVertIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                    <Menu
                      anchorEl={eventMenuAnchor}
                      open={Boolean(eventMenuAnchor)}
                      onClose={() => setEventMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{ zIndex: 10006 }} // Above EventSheet
                    >
                      <MenuItem 
                        onClick={() => {
                          handleUnhelpfulEvent();
                          setEventMenuAnchor(null);
                        }}
                        disabled={event.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString())}
                      >
                        <ThumbDownIcon sx={{ fontSize: '16px', mr: 1 }} />
                        Unhelpful
                      </MenuItem>
                      <MenuItem onClick={() => {
                        setEventMenuAnchor(null);
                        setFlagAbuseDialog({ open: true, targetId: event._id, targetType: 'event' });
                      }}>
                        <FlagIcon sx={{ fontSize: '16px', mr: 1 }} />
                        Flag Abuse
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
            
        
          </Box>

          {/* Store Title and Actions */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" gutterBottom>
              {store.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {store.address?.fullAddress && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: '200px', sm: '300px' }
                  }}
                >
                  {store.address.fullAddress}
                </Typography>
              )}
              {canNavigate && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NavigationIcon />}
                  onClick={handleNavigate}
                  sx={{ flexShrink: 0 }}
                >
                  Navigate
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Image Gallery - All Users' Photos */}
          {allPhotos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <EventImageGallery
                photos={allPhotos}
                getImageUrl={getImageUrl}
                eventId={event._id}
                event={event}
                getUserAvatar={getUserAvatar}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Discussion - Updates */}
          {/* <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
            Discussion
          </Typography> */}

          {event.updates && event.updates.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              {event.updates.map((update, index) => (
                <Box
                  key={index}
                  sx={{
                    py: 1.5,
                    borderBottom: index < event.updates.length - 1 ? '1px solid #e0e0e0' : 'none',
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <UserIdentity
                          user={typeof update.userId === 'object' ? update.userId : { username: update.username, _id: update.userId }}
                          size="sm"
                          variant="light"
                          dense
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {formatRelativeTime(update.createdAt)}
                        </Typography>
                      </Box>
                      {update.content && (
                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.875rem', lineHeight: 1.4 }}>
                          {update.content}
                        </Typography>
                      )}
                      {update.photos && update.photos.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                          {/* Show max 2 photos in a row, then +N */}
                          {update.photos.slice(0, 2).map((photo, photoIndex) => (
                            <Box
                              key={photoIndex}
                              sx={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '1px solid #e0e0e0'
                              }}
                              onClick={() => {
                                // Open slideshow for this update's photos
                                const startIndex = update.photos.findIndex(p => p.url === photo.url);
                                setSlideshowPhotos(update.photos);
                                setSlideshowIndex(startIndex >= 0 ? startIndex : 0);
                                setShowSlideshow(true);
                              }}
                            >
                              <img
                                src={getImageUrl(photo.url)}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </Box>
                          ))}
                          {update.photos.length > 2 && (
                            <Box
                              sx={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#e0e0e0',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#666',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                // Open slideshow for all this update's photos
                                setSlideshowPhotos(update.photos);
                                setSlideshowIndex(0);
                                setShowSlideshow(true);
                              }}
                            >
                              +{update.photos.length - 2}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
                      {/* Show hearts for photo posts, helpful/unhelpful for text-only */}
                      {update.photos && update.photos.length > 0 ? (
                        // Photo post - show heart button
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleHeartUpdate(index)}
                            disabled={update.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString())}
                            sx={{ 
                              color: update.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString()) ? 'pink' : 'inherit',
                              padding: '4px',
                              '& .MuiSvgIcon-root': { fontSize: '16px' }
                            }}
                          >
                            <FavoriteIcon />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: '16px', textAlign: 'center', fontSize: '0.75rem' }}>
                            {update.heartedBy?.length || 0}
                          </Typography>
                          {/* 3-dot menu for photo posts */}
                          <IconButton
                            size="small"
                            onClick={(e) => setUpdateMenuAnchors({ ...updateMenuAnchors, [index]: e.currentTarget })}
                            sx={{ padding: '4px', ml: 0.5 }}
                          >
                            <MoreVertIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                          <Menu
                            anchorEl={updateMenuAnchors[index]}
                            open={Boolean(updateMenuAnchors[index])}
                            onClose={() => setUpdateMenuAnchors({ ...updateMenuAnchors, [index]: null })}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            sx={{ zIndex: 10006 }} // Above EventSheet
                            MenuListProps={{
                              sx: { zIndex: 10006 }
                            }}
                            PaperProps={{
                              sx: { zIndex: 10006 }
                            }}
                          >
                            <MenuItem 
                              onClick={() => {
                                handleUnhelpfulUpdate(index);
                                setUpdateMenuAnchors({ ...updateMenuAnchors, [index]: null });
                              }}
                              disabled={update.heartedBy?.some(h => h.userId?.toString() === currentUserId?.toString())}
                            >
                              <ThumbDownIcon sx={{ fontSize: '16px', mr: 1 }} />
                              Unhelpful
                            </MenuItem>
                            <MenuItem onClick={() => {
                              setUpdateMenuAnchors({ ...updateMenuAnchors, [index]: null });
                              setFlagAbuseDialog({ open: true, targetId: index, targetType: 'update' });
                            }}>
                              <FlagIcon sx={{ fontSize: '16px', mr: 1 }} />
                              Flag Abuse
                            </MenuItem>
                          </Menu>
                        </>
                      ) : (
                        // Text-only post - show helpful/unhelpful buttons
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleVote(index, true)}
                            sx={{ 
                              color: currentUserId && update.helpfulBy?.some(id => id.toString() === currentUserId.toString()) ? 'green' : 'inherit',
                              padding: '4px',
                              '& .MuiSvgIcon-root': { fontSize: '16px' }
                            }}
                          >
                            <ThumbUpIcon />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: '16px', textAlign: 'center', fontSize: '0.75rem' }}>
                            {update.helpfulCount || 0}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleVote(index, false)}
                            sx={{ 
                              color: currentUserId && update.unhelpfulBy?.some(id => id.toString() === currentUserId.toString()) ? 'red' : 'inherit',
                              padding: '4px',
                              '& .MuiSvgIcon-root': { fontSize: '16px' }
                            }}
                          >
                            <ThumbDownIcon />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: '16px', textAlign: 'center', fontSize: '0.75rem' }}>
                            {update.unhelpfulCount || 0}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
              No updates yet. Be the first to post!
            </Typography>
          )}

          {/* Add Update Section - Redesigned */}
          <Divider sx={{ my: 3 }} />
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              p: 2,
              backgroundColor: '#fafafa'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
              Comment / Sale Update
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment or update..."
              variant="outlined"
              sx={{ 
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px'
                }
              }}
            />

            <Box sx={{ mb: 1.5 }}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload"
              />
              {photos.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  {photos.map((photo, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <img
                        src={getImageUrl(photo.url)}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => document.getElementById('photo-upload').click()}
                sx={{
                  borderColor: '#d0d0d0',
                  color: 'text.secondary',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#b0b0b0',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              >
                Add Photos
              </Button>
            </Box>

            <Button
              variant="contained"
              onClick={handleAddUpdate}
              disabled={loading || (!comment && photos.length === 0)}
              startIcon={<CommentIcon />}
              fullWidth
              sx={{ 
                backgroundColor: '#202E40',
                textTransform: 'none',
                borderRadius: '8px',
                py: 1,
                '&:hover': {
                  backgroundColor: '#2a3d55'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </motion.div>

      {/* Slideshow */}
      {showSlideshow && (
        <EventImageSlideshow
          photos={slideshowPhotos}
          initialIndex={slideshowIndex}
          getImageUrl={getImageUrl}
          eventId={event._id}
          event={event}
          description={slideshowDescription}
          getUserAvatar={getUserAvatar}
          onClose={() => {
            setShowSlideshow(false);
            setSlideshowDescription(null);
          }}
        />
      )}

      {/* Flag Abuse Dialog */}
      <Dialog
        open={flagAbuseDialog.open}
        onClose={() => {
          setFlagAbuseDialog({ open: false, targetId: null, targetType: null });
          setFlagAbuseComment('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Flag Abuse</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={flagAbuseComment}
            onChange={(e) => setFlagAbuseComment(e.target.value)}
            placeholder="Please describe why you're flagging this content..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setFlagAbuseDialog({ open: false, targetId: null, targetType: null });
            setFlagAbuseComment('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleFlagAbuse} variant="contained" color="error">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventSheet;





